/**
 * @file keyboard-performance.ts
 * @author Rapoo Device SDK Team
 * @date 2026-07-23
 * @description Telink 键盘性能参数实现
 *
 * 键盘通过 HID EEPROM 协议读写回报率/休眠时间等性能参数。
 * 回报率偏移地址 0x002C，休眠时间偏移地址 0x0030。
 */

import { keyboardPerfCfgFeature } from '../../../../features/keyboard-performance/contract'
import type { KeyboardPerfCfg, USBReportRate, SleepTime } from '../../../../types/keyboardPerCfg'
import { ADDRESS_TELINK } from '../../protocols/address'
import { sendRead, sendWrite } from '../../capabilities/memory'
import { getAddress } from '../../../../utils/common'

export class TelinkKeyboardPerfCfg extends keyboardPerfCfgFeature {
  // getMacMode
  getMacMode(): Promise<boolean> {
    const ADDRESS = getAddress(ADDRESS_TELINK.SYS_MODE)
    return sendRead(this.device, ADDRESS, 8, (data: DataView) => {
      return data.getUint8(4) === 1
    })
  }

  // ==================== 回报率 ====================
  async getReportRate(): Promise<USBReportRate> {
    const ADDRESS = getAddress(ADDRESS_TELINK.REPORT_RATE, this.device.currentConfig.index)
    // 读 8 字节（A4 对齐），地址 +2 为 USB 回报率
    return sendRead(this.device, ADDRESS, 8, (res: DataView) => {
      return res.getUint8(4 + 2) as USBReportRate
    })
  }

  async setReportRate(rate: USBReportRate): Promise<void> {
    const ADDRESS = getAddress(ADDRESS_TELINK.REPORT_RATE, this.device.currentConfig.index)
    // 先读当前 4 字节，修改 byte0(2.4G) 和 byte2(USB) 后回写
    const buf = await sendRead(this.device, ADDRESS, 8, (res: DataView) => {
      const b0 = res.getUint8(4)
      const b1 = res.getUint8(5)
      const b2 = res.getUint8(6)
      const b3 = res.getUint8(7)
      return [b0, b1, b2, b3]
    })
    // byte2 = 新回报率, byte0 = byte2（同步 2.4G 回报率）
    return sendWrite(this.device, ADDRESS, [rate, buf[1], rate, buf[3]])
  }

  // ==================== 休眠时间 ====================
  async getSleepTime(): Promise<SleepTime> {
    const ADDRESS = getAddress(ADDRESS_TELINK.SLEEP_TIME, this.device.currentConfig.index)
    // Telink A4 指令只支持 8 的倍数读取长度，读 8 字节取第 0 字节
    return sendRead(this.device, ADDRESS, 8, (res: DataView) => {
      return res.getUint8(4) as SleepTime
    })
  }

  async setSleepTime(time: SleepTime): Promise<void> {
    const ADDRESS = getAddress(ADDRESS_TELINK.SLEEP_TIME, this.device.currentConfig.index)
    return sendWrite(this.device, ADDRESS, [time])
  }

  // ==================== 批量获取 ====================
  async getAllPerfCfg(): Promise<KeyboardPerfCfg> {
    const cfg: KeyboardPerfCfg = {}

    // 回报率地址 0x002C + 休眠地址 0x0030，一次读取 8 字节覆盖
    const ADDRESS = getAddress(ADDRESS_TELINK.REPORT_RATE, this.device.currentConfig.index)
    await sendRead(
      this.device,
      ADDRESS,
      8,
      (res: DataView) => {
        cfg.usbReportRate = res.getUint8(4 + 2) as USBReportRate
        cfg.sleepTime = res.getUint8(8) as SleepTime
      },
      [`cfg:${this.device.currentConfig.index}`],
    )

    return cfg
  }

  // ==================== 批量设置 ====================
  async setAllPerfCfg(cfg: KeyboardPerfCfg): Promise<void> {
    if (cfg.usbReportRate !== undefined) {
      await this.setReportRate(cfg.usbReportRate as USBReportRate)
    }
    if (cfg.sleepTime !== undefined) {
      await this.setSleepTime(cfg.sleepTime)
    }

    this.device.cache.invalidateByTag(`cfg:${this.device.currentConfig.index}`)
  }
}
